import { createContext, type ReactElement, useContext, useEffect, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useCustomerly } from 'react-live-chat-customerly'
import { useTggl } from 'react-tggl-client'

import type { Socket } from 'socket.io-client'

import { ApiCaller } from '@frontend/api'
import type { Application, Brain, Chat, UserMe } from '@types'
import { useSentry } from '@utils/frontend'

import env from '../environments'
import { socket } from '../socket'
import AppProvider from './appContext'

interface GlobalContextProps {
	readonly children: React.ReactNode;
	readonly onLoaded: () => void;
	readonly setIsVersionMismatch: (value: boolean) => void;
}

interface IGlobalContext {
	readonly socket: Socket;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const GlobalContext = createContext<IGlobalContext | undefined>(undefined)

export default function GlobalContextProvider({ children, onLoaded, setIsVersionMismatch }: GlobalContextProps) {
	const [me, setMe] = useState<UserMe | undefined>(undefined)
	const [chats, setChats] = useState<Chat[]>([])
	const [brains, setBrains] = useState<Brain[]>([])
	const [applications, setApplications] = useState<Application[]>([])

	const { updateContext } = useTggl()
	const { load } = useCustomerly()
	const { setUser, setNodeEnv, captureException } = useSentry()

	useEffect(() => {
		async function healthCheck() {
			try {
				const { version } = await ApiCaller.healthcheck()

				if (version !== env.version) {
					setIsVersionMismatch(true)
				}
			} catch (err) {
				captureException(err)

				return
			}
		}

		socket.on('version:control', healthCheck)
		socket.connect()

		return () => {
			socket.off('version:control', healthCheck)
		}
	}, [])

	useEffect(() => {
		async function loadContext() {
			try {
				const me = await ApiCaller.getMe()
				const chats = await ApiCaller.getAllChats()
				const brains = await ApiCaller.getBrains()
				const applications = await ApiCaller.getAllApplications()

				setMe(me)
				setChats(chats)
				setBrains(brains)
				setApplications(applications)

				// Set context for Tggl
				updateContext({
					environment: env.appEnv,
					userId: me.id,
					email: me.email,
					companyId: me.company?.id,
					companySlug: me.company?.slug
				})

				// Set context for Sentry
				setUser({ userId: me.id, email: me.email, companyId: me.company?.id, companySlug: me.company?.slug })
				setNodeEnv(env.appEnv as 'development' | 'production')

				// Set context for Customerly
				load({
					// eslint-disable-next-line @typescript-eslint/naming-convention
					user_id: me.id,
					email: me.email,
					name: me.name,
					// eslint-disable-next-line @typescript-eslint/naming-convention
					company: me.company ? { company_id: me.company.id, name: me.company?.name } : undefined,
					visible: false,
					disableCSSOMInjection: true,
					position: {
						desktop: {
							bottom: -60,
							side: 0
						},
						mobile: {
							bottom: 0,
							side: 0
						}
					}
				})

				onLoaded()
			} catch (error) {
				captureException(error)
				toast.error('An error occurred while loading the application. Please try again later.')
			}
		}

		loadContext()
	}, [])

	if (!me) {
		return children as ReactElement
	}

	return (
		<GlobalContext.Provider
			value={ {
				socket
			} }
		>
			<AppProvider
				me={ me }
				socket={ socket }
				initialChats={ chats }
				initialBrains={ brains }
				initialApplications={ applications }
			>
				{ children }
			</AppProvider>
		</GlobalContext.Provider>
	)
}

export function useGlobalContext() {
	const context = useContext(GlobalContext)

	if (!context) {
		throw new Error('useAppContext must be used within a GlobalProvider')
	}

	return context
}

